import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { brands } from '../data/brands';
import { Listing, ListingFilters } from '../lib/listings';
import { searchListings } from '../lib/search';
import { thumbUrl } from '../lib/images';
import { getBrandImageUrl } from '../lib/brandImages';
import LikeButton from '../components/LikeButton';
import SEO, { SITE_URL } from '../components/SEO';

type BrandFilters = Pick<ListingFilters, 'sort' | 'color' | 'condition' | 'type'>;

function BrandPage() {
  const { brandId } = useParams<{ brandId: string }>();
  const brand = brandId ? brands[brandId] : null;
  const PAGE_SIZE = 24;
  const [listings, setListings] = useState<Listing[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [filters, setFilters] = useState<BrandFilters>({ sort: 'newest' });

  useEffect(() => {
    if (brandId) {
      loadBrandListings(brandId, filters, 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandId, filters]);

  const loadBrandListings = async (brandSlug: string, activeFilters: BrandFilters, offset: number) => {
    if (offset === 0) setLoading(true);
    else setLoadingMore(true);
    try {
      const { listings: pageListings, total, error } = await searchListings(
        { brand: [brandSlug], ...activeFilters },
        { limit: PAGE_SIZE, offset },
      );
      if (error) throw error;
      setTotal(total);
      setListings((prev) => (offset === 0 ? pageListings : [...prev, ...pageListings]));
    } catch (error) {
      console.error('Error loading brand listings:', error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const handleLoadMore = () => {
    if (brandId) loadBrandListings(brandId, filters, listings.length);
  };

  const handleFilterChange = (key: keyof BrandFilters, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value || undefined }));
  };

  if (!brand) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <SEO title="Brand not found" noindex />
        <p className="text-center text-gray-500">Brand not found</p>
      </div>
    );
  }

  const canonical = `/brand/${brand.id}`;
  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
      { '@type': 'ListItem', position: 2, name: `${brand.name} Wigs`, item: `${SITE_URL}${canonical}` },
    ],
  };

  return (
    <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <SEO
        title={`${brand.name} Wigs — Buy, Sell & Trade Pre-Owned`}
        description={`Buy, sell & trade ${brand.name} wigs on WigConnection — pre-owned, open-box and outlet-priced deals below retail. ${brand.description}`.slice(0, 300)}
        canonical={canonical}
        jsonLd={breadcrumbJsonLd}
      />

      {/* Brand Header */}
      <div className="bg-white rounded-xl shadow-sm p-8 mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-4">{brand.name}</h1>
        <p className="text-lg text-gray-600 mb-6">{brand.description}</p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h2 className="text-lg font-semibold mb-3">Features</h2>
            <ul className="space-y-2">
              {brand.features.map((feature, index) => (
                <li key={index} className="flex items-center text-gray-600">
                  <span className="mr-2">•</span>
                  {feature}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h2 className="text-lg font-semibold mb-3">Price Range</h2>
            <p className="text-gray-600">{brand.priceRange}</p>
          </div>
        </div>
      </div>

      {/* Filters (wired to the public catalog RPC) */}
      <div className="bg-white p-4 rounded-lg shadow-sm mb-6">
        <div className="flex flex-wrap gap-4">
          <select
            className="rounded-md border-gray-300 text-sm"
            value={filters.sort || 'newest'}
            onChange={(e) => handleFilterChange('sort', e.target.value)}
            aria-label="Sort by"
          >
            <option value="newest">Newest</option>
            <option value="price_asc">Price: Low to High</option>
            <option value="price_desc">Price: High to Low</option>
          </select>
          <select
            className="rounded-md border-gray-300 text-sm"
            value={filters.color || ''}
            onChange={(e) => handleFilterChange('color', e.target.value)}
            aria-label="Color"
          >
            <option value="">All Colors</option>
            <option value="Black">Black</option>
            <option value="Brunette">Brunette</option>
            <option value="Blonde">Blonde</option>
            <option value="Red">Red</option>
            <option value="Gray/Silver">Gray/Silver</option>
            <option value="White">White</option>
          </select>
          <select
            className="rounded-md border-gray-300 text-sm"
            value={filters.condition || ''}
            onChange={(e) => handleFilterChange('condition', e.target.value)}
            aria-label="Condition"
          >
            <option value="">Any Condition</option>
            <option value="new">New with tags</option>
            <option value="like-new">Like new</option>
            <option value="gently-used">Gently used</option>
            <option value="well-used">Well used</option>
          </select>
          <select
            className="rounded-md border-gray-300 text-sm"
            value={filters.type || ''}
            onChange={(e) => handleFilterChange('type', e.target.value)}
            aria-label="Listing type"
          >
            <option value="">All Types</option>
            <option value="sale">For Sale</option>
            <option value="trade">For Trade</option>
            <option value="both">Sale or Trade</option>
          </select>
        </div>
      </div>

      {/* Products Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[...Array(6)].map((_, index) => (
            <div key={index} className="bg-white rounded-lg shadow-md overflow-hidden animate-pulse">
              <div className="relative pb-[100%] bg-gray-200" />
              <div className="p-4 space-y-2">
                <div className="h-4 bg-gray-200 rounded w-3/4" />
                <div className="h-4 bg-gray-200 rounded w-1/2" />
              </div>
            </div>
          ))}
        </div>
      ) : listings.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {listings.map((listing) => (
            <Link
              key={listing.id}
              to={`/listing/${listing.slug || listing.id}`}
              className="bg-white rounded-lg shadow-md overflow-hidden transform hover:scale-[1.02] transition-transform duration-300"
            >
              <div className="relative pb-[100%]">
                <img
                  loading="lazy"
                  decoding="async"
                  src={thumbUrl(listing.image_urls?.[0] || getBrandImageUrl(listing.brand), 600)}
                  alt={listing.title}
                  className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute top-2 right-2">
                  <LikeButton listingId={listing.id} className="bg-white rounded-full p-1.5 shadow-md" />
                </div>
              </div>
              <div className="p-4">
                <h3 className="font-medium mb-2">{listing.title}</h3>
                <div className="flex items-center justify-between">
                  <span className="text-lg font-bold">${listing.price}</span>
                  <div className="flex items-center">
                    <span className="text-sm text-gray-600">{listing.condition}</span>
                  </div>
                </div>
                <div className="mt-4">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    listing.type === 'sale' ? 'bg-green-100 text-green-800' :
                    listing.type === 'trade' ? 'bg-blue-100 text-blue-800' :
                    'bg-purple-100 text-purple-800'
                  }`}>
                    {listing.type === 'sale' ? 'For Sale' :
                     listing.type === 'trade' ? 'For Trade' :
                     'Sale or Trade'}
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      ) : (
        <div className="text-center py-12">
          <p className="text-gray-500">No listings found for this brand.</p>
        </div>
      )}

      {!loading && listings.length < total && (
        <div className="mt-8 text-center">
          <button
            onClick={handleLoadMore}
            disabled={loadingMore}
            className="px-6 py-2 text-sm font-medium rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            {loadingMore ? 'Loading...' : `Load more (${listings.length} of ${total})`}
          </button>
        </div>
      )}
    </main>
  );
}

export default BrandPage;
